import LeadStaffPage from '../pages/leads/LeadStaffPage';
// apps/client/src/routes/AppRoutes.tsx (CONVERTED TO TYPESCRIPT)

import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams, useOutletContext, Outlet } from 'react-router-dom';

// Guards
import ProtectedRoute from './ProtectedRoute';
import MfaEnforcementGuard from '../components/common/MfaEnforcementGuard';
import RequireRole from './guards/RequireRole';
import RequireCapability from './guards/RequireCapability';
import RequireLeadTab from './guards/RequireLeadTab';
import RequireCommissionAccess from './guards/RequireCommissionAccess';
import RequireReportAccess from './guards/RequireReportAccess';
import RequireClientCareAccess from './guards/RequireClientCareAccess';
import RequireTenantReport from './guards/RequireTenantReport';
import RequireEdmEnabled from './guards/RequireEdmEnabled';
import RequireEdmImportAccess from './guards/RequireEdmImportAccess';
import RequireGrowthPropose from './guards/RequireGrowthPropose';
import { FeatureGate } from '../components/FeatureGate';

// Layout
import { AppLayout } from '../components/layout/AppLayout';

// Pages
import DashboardPage from '../pages/DashboardPage';
import MonthlyTableDashboardPage from '../pages/MonthlyTableDashboardPage';
import OfficeDashboardPage from '../pages/OfficeDashboardPage';
import UploadPage from '../pages/UploadPage';
import TeamManagementSwitch from '../pages/team/TeamManagementSwitch';
import OfficeManagementPage from '../pages/offices/OfficeManagementPage';
import OfficeComparisonPage from '../pages/OfficeComparisonPage';
import LoanBooksPage from '../pages/loan-books/LoanBooksPage';
import BrokerManagementPage from '../pages/offices/BrokerManagementPage';
import { LoanListPage } from '../pages/LoanListPage';

// Report Pages
import SettlementAgeReportPage from '../pages/reports/SettlementAgeReportPage';
import ClawbackPage from '../pages/reports/ClawbackPage';
import ArrearsPage from '../pages/reports/ArrearsPage';
import TrailRunOffReportPage from '../pages/reports/TrailRunOffReportPage';
import TrailBookValuationPage from '../pages/reports/TrailBookValuationPage';
import GroupSnapshotPage from '../pages/reports/GroupSnapshotPage';
import TrailDropOffPage from '../pages/reports/TrailDropOffPage';
import ApprovedVsSettledRouter from '../pages/reports/ApprovedVsSettledRouter';
import LinkedDealsPage from '../pages/reports/LinkedDealsPage';
import TrailForecastPage from '../pages/TrailForecastPage';
import CustomerPortfolioPage from '../pages/reports/CustomerPortfolioPage';
import ClientSetsPage from '../pages/ClientSetsPage';
import DischargedLoansRouter from '../pages/reports/DischargedLoansRouter';
import BookHealthReportPage from '../pages/reports/BookHealthReportPage';
import RctiCashflowPage from '../pages/reports/RctiCashflowPage';
// Settings & Admin Pages
import SettingsPage from '../pages/settings/SettingsPage';
import BillingPage from '../pages/settings/BillingPage';
import ConnectedAccountsPage from '../pages/settings/ConnectedAccountsPage';
import AuditLogPage from '../pages/admin/AuditLogPage';

// Acquisition Analysis (Enterprise Feature)
import AcquisitionAnalysisPage from '../pages/AcquisitionAnalysisPage';
import SharedAnalysisPage from '../pages/SharedAnalysisPage';

// Shared Trail Book Valuation (Public Share Link)
import SharedTrailBookValuationPage from '../pages/reports/SharedTrailBookValuationPage';

// Succession Readiness Report (Pro Feature)
import SuccessionReadinessReportPage from '../pages/reports/SuccessionReadinessReportPage';
import SharedSuccessionReadinessPage from '../pages/reports/SharedSuccessionReadinessPage';

// Shared Company Overview (VALUTTEN-branded read-only snapshot — parent groups e.g. Recludo)
import SharedCompanyOverviewPage from '../pages/SharedCompanyOverviewPage';

// Referral Partner Report (Pro Feature)
import ReferralPartnerPage from '../pages/reports/ReferralPartnerPage';

// Broker Activity — Trail Leakage + CRM Integrations + Leads + Activity Log (Enterprise add-on)
// Trail Leakage now lives at /reports/trail-leakage (the canonical Reports
// surface); its definition banner is rendered inside the reports page itself.
import TrailLeakagePage from '../pages/reports/TrailLeakagePage';
import IntegrationsPage from '../pages/settings/IntegrationsPage';
import ZohoOAuthCallbackPage from '../pages/settings/ZohoOAuthCallbackPage';
import CrmMappingsPage from '../pages/settings/CrmMappingsPage';
import LeadsPage from '../pages/leads/LeadsPage';
import LeadAttributionPage from '../pages/leads/LeadAttributionPage';
import MarketingSourceReportPage from '../pages/leads/MarketingSourceReportPage';
import SharedMarketingSourceReportPage from '../pages/reports/SharedMarketingSourceReportPage';
import LeadCaptureSetupWizardPage from '../pages/leads/LeadCaptureSetupWizardPage';
import LeadCaptureIntegrationsPage from '../pages/leads/LeadCaptureIntegrationsPage';
import LeadCaptureHowItWorksPage from '../pages/leads/LeadCaptureHowItWorksPage';
import LeadCaptureCostSavingsPage from '../pages/leads/LeadCaptureCostSavingsPage';
import LeadCaptureTemplatesPage from '../pages/leads/LeadCaptureTemplatesPage';
import LeadCaptureSourcesPage from '../pages/leads/LeadCaptureSourcesPage';
import LeadCaptureEmailPage from '../pages/leads/LeadCaptureEmailPage';
import MyAvailabilityPage from '../pages/leads/MyAvailabilityPage';
import LeadRoutingRulesPage from '../pages/leads/LeadRoutingRulesPage';
import LeadTaxonomyPage from '../pages/leads/LeadTaxonomyPage';
import LeadPipelinePage from '../pages/leads/LeadPipelinePage';
import CrmStageMappingsPage from '../pages/leads/CrmStageMappingsPage';
import PipelinePage from '../pages/broker-activity/PipelinePage';
import ScorecardPage from '../pages/broker-activity/ScorecardPage';
import WeeklyFunnelPage from '../pages/broker-activity/WeeklyFunnelPage';
import LodgementsPage from '../pages/broker-activity/LodgementsPage';
import BrokerActivityOverviewPage from '../pages/broker-activity/OverviewPage';
import LeadSlaDashboardPage from '../pages/leads/LeadSlaDashboardPage';
import GrowthInboxPage from '../pages/leads/GrowthInboxPage';
import EdmCampaignsPage from '../pages/leads/edm/EdmCampaignsPage';
import EdmCampaignPage from '../pages/leads/edm/EdmCampaignPage';
import EdmImportPage from '../pages/leads/edm/EdmImportPage';
import WebsiteConnectionsPage from '../pages/leads/website/WebsiteConnectionsPage';
import ConsentTextsPage from '../pages/leads/consent/ConsentTextsPage';
import UnsubscribesPage from '../pages/leads/unsubscribes/UnsubscribesPage';
import EdmContactsPage from '../pages/leads/edm/EdmContactsPage';
import EdmDomainPage from '../pages/leads/edm/EdmDomainPage';
import EdmTopicsPage from '../pages/leads/edm/EdmTopicsPage';
import EdmJourneysPage from '../pages/leads/edm/EdmJourneysPage';
import EdmJourneyPage from '../pages/leads/edm/EdmJourneyPage';
import PipelineTrackerPage from '../pages/pipeline-tracker/PipelineTrackerPage';
import AddOnSubappLayout from '../components/layout/AddOnSubappLayout';
import ActivitiesPage from '../pages/activities/ActivitiesPage';

// Refinance Intelligence (Loan Book Conversion Tracking)
import RefinanceIntelligenceRouter from '../pages/reports/RefinanceIntelligenceRouter';

// Meta Messenger / Page DM Conversations (per-tenant flag-gated;
// see canonical plan meta-messenger-webhook-workstream.md §1.11)
import ConversationsInboxPage from '../pages/conversations/ConversationsInboxPage';
import ConversationThreadPage from '../pages/conversations/ConversationThreadPage';

// Acquired Books Page (Detailed view by office)
import AcquiredBooksRouter from '../pages/reports/AcquiredBooksRouter';

// Broker Loan Books (Cross-template analysis)
import BrokerLoanBooksPage from '../pages/reports/BrokerLoanBooksPage';

// Broker Performance Report (per-broker detail view)
import BrokerReportPage from '../pages/reports/BrokerReportPage';

// Recludo Fund-Flow Report (admin) — VFA pilot, opt-in per tenant
import RecludoReportsAdminPage from '../pages/reports/RecludoReportsAdminPage';

// Book Value Report (admin) — per-broker outstanding loan balances; opt-in per tenant
import BookValueReportPage from '../pages/reports/BookValueReportPage';

// Broker Book Trend Report — per-broker monthly book trend (VFA pilot)
import BrokerBookTrendReportPage from '../pages/reports/BrokerBookTrendReportPage';

// Shared Acquired Book Performance (Public Share Link)
import SharedAcquiredBookPage from '../pages/SharedAcquiredBookPage';

// Shared Broker Report (Public Share Link)
import SharedBrokerReportPage from '../pages/SharedBrokerReportPage';

// Shared Recludo Fund-Flow Report (Public Share Link — VFA pilot)
import SharedRecludoFundFlowPage from '../pages/SharedRecludoFundFlowPage';

// Shared Book Value Report (public, token-validated; opt-in per tenant)
import SharedBookValueReportPage from '../pages/SharedBookValueReportPage';

// Contact Activity Page
import ContactActivityPage from '../pages/ContactActivityPage';
import ClientDatabasePage from '../pages/clients/ClientDatabasePage';
import ClientCareSettingsPage from '../pages/clients/ClientCareSettingsPage';
import ReengagementSettingsPage from '../pages/settings/ReengagementSettingsPage';
import PartnerReferralsPage from '../pages/partner-referrals/PartnerReferralsPage';
import PartnerOverviewTab from '../pages/partner-referrals/tabs/OverviewTab';
import PartnerReferTab from '../pages/partner-referrals/tabs/ReferTab';
import PartnerClientsTab from '../pages/partner-referrals/tabs/ClientsTab';
import PartnerReferralDetailPage from '../pages/partner-referrals/tabs/ReferralDetailPage';
import PartnerComplianceTab from '../pages/partner-referrals/tabs/ComplianceTab';
import PartnerBackBookRadarTab from '../pages/partner-referrals/radar/BackBookRadarTab';
import PartnerRewardsTab from '../pages/partner-referrals/tabs/RewardsTab';
import RequirePartnerReferralsArea from '../pages/partner-referrals/my-program/RequirePartnerReferralsArea';
import MyReferralProgramPage from '../pages/partner-referrals/my-program/MyReferralProgramPage';

// Broker Commission Settings Pages
import BrokerCommissionSettingsPage from '../pages/brokers/BrokerCommissionSettingsPage';
import BrokerCommissionViewPage from '../pages/brokers/BrokerCommissionViewPage';

// AI Pages
import AIAssistantPage from '../pages/ai/AIAssistantPage';
import FirstLoginBriefingPage from '../pages/briefing/FirstLoginBriefingPage';

// Loyalty Pages
import LoyaltyDashboardPage from '../pages/loyalty/LoyaltyDashboardPage';
import LoyaltyClientsPage from '../pages/loyalty/LoyaltyClientsPage';
import LoyaltyRedemptionsPage from '../pages/loyalty/LoyaltyRedemptionsPage';
import LoyaltySettingsPage from '../pages/loyalty/LoyaltySettingsPage';
import BulkActivationPage from '../pages/loyalty/BulkActivationPage';

// Auth Pages
import LoginPage from '../pages/auth/LoginPage';
import AcceptInvitationPage from '../AcceptInvitationPage';
import PasswordResetPage from '../pages/auth/PasswordResetPage';
// Native GCIP TOTP enrolment — the go-forward enrolment surface. The bespoke
// Enroll2FAPage file remains in the repo, unrouted, until the Phase-9
// decommission. The sign-in CHALLENGE is native too, rendered INLINE on
// LoginPage (the resolver is a live object). /verify-2fa stays on the bespoke
// Verify2FAPage so not-yet-migrated legacy-enrolled users are still challenged
// until the Phase 5-7 re-enrolment wave retires the bespoke path.
import NativeEnroll2FAPage from '../pages/auth/NativeEnroll2FAPage';
import Verify2FAPage from '../pages/auth/Verify2FAPage';
import AuthActionHandler from '../pages/auth/AuthActionHandler'; // NEW: Import the handler
import CustomPasswordResetHandler from '../pages/auth/CustomPasswordResetHandler'; // Custom password reset handler

// Onboarding Pages
import ActivationPage from '../pages/onboarding/ActivationPage'; // White-glove activation (Email Scout)

// Subscription Pages (Post-Stripe Checkout)
import SubscriptionSuccessPage from '../pages/subscription/SubscriptionSuccessPage';
import SubscriptionCanceledPage from '../pages/subscription/SubscriptionCanceledPage';
import PilotWelcomePage from '../pages/subscription/PilotWelcomePage';

// Help & Support
import HelpPage from '../pages/help/HelpPage';

// Cookie Consent
import CookieConsentBanner from '../components/common/CookieConsentBanner';

// Other Pages
import LegalRedirect from '../components/common/LegalRedirect';
import { LEGAL_URLS } from '../config/legalUrls';
import NotFoundPage from '../pages/errors/NotFoundPage';

/**
 * Legacy deep-link redirect: the DM Inbox moved from the standalone
 * `/conversations/*` tree into the Lead Capture CRM subapp at
 * `/leads/dm-inbox/*` (2026-05-25). Preserve the conversation id so old
 * bookmarks / notification links land on the right thread rather than the
 * inbox root.
 */
const ConversationDeepLinkRedirect: React.FC = () => {
  const { conversationId } = useParams<{ conversationId: string }>();
  return <Navigate to={`/leads/dm-inbox/${conversationId ?? ''}`} replace />;
};

// CONVERTED: Component is now explicitly typed as a React Functional Component.
const AppRoutes: React.FC = () => {
  return (
    <BrowserRouter>
      <Routes>
        {/* --- Public Routes --- */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/password-reset" element={<PasswordResetPage />} />
        <Route path="/reset-password" element={<CustomPasswordResetHandler />} /> {/* Custom password reset handler */}
        <Route path="/handle-auth-action" element={<AuthActionHandler />} />
        <Route path="/__/auth/action" element={<AuthActionHandler />} /> {/* Firebase auth action handler */}
        <Route
          path="/accept-invitation"
          element={<AcceptInvitationPage />}
        />
        <Route path="/verify-2fa" element={<Verify2FAPage />} />

        {/* Activation Page - Semi-protected (requires auth but not full onboarding) */}
        {/* Part of White-Glove Onboarding: First customer interaction after Stripe checkout */}
        <Route path="/activation" element={<ActivationPage />} />

        {/* Subscription Pages - Post-Stripe Checkout (no auth required) */}
        <Route path="/subscription/success" element={<SubscriptionSuccessPage />} />
        <Route path="/subscription/canceled" element={<SubscriptionCanceledPage />} />

        {/* Manually-onboarded Pilot Confirmation (no self-service; admin provisions account) */}
        <Route path="/welcome/pilot" element={<PilotWelcomePage />} />

        {/* Legal documents live on valutten.com only (config/legalUrls). */}
        <Route path="/terms" element={<LegalRedirect to={LEGAL_URLS.terms} />} />
        <Route path="/privacy" element={<LegalRedirect to={LEGAL_URLS.privacy} />} />
        <Route path="/cookie-notice" element={<LegalRedirect to={LEGAL_URLS.cookies} />} />

        {/* Shared Analysis - Public Route (no auth required) */}
        <Route path="/acquisition-analysis/shared/:token" element={<SharedAnalysisPage />} />

        {/* Shared Trail Book Valuation - Public Route (no auth required) */}
        <Route path="/reports/trail-book-valuation/shared/:token" element={<SharedTrailBookValuationPage />} />

        {/* Shared Acquired Book Performance - Public Route (no auth required) */}
        <Route path="/refinance/acquired-book/shared/:token" element={<SharedAcquiredBookPage />} />

        {/* Shared Broker Report - Public Route (no auth required) */}
        <Route path="/reports/broker-report/shared/:token" element={<SharedBrokerReportPage />} />

        {/* Shared Recludo Fund-Flow Report - Public Route (no auth required) */}
        <Route path="/reports/recludo/shared/:token" element={<SharedRecludoFundFlowPage />} />

        {/* Shared Book Value Report - Public Route (token-validated, no auth required) */}
        <Route path="/reports/book-value/:token" element={<SharedBookValueReportPage />} />

        {/* Shared Succession Readiness Report - Public Route (no auth required) */}
        <Route path="/reports/succession-readiness/shared/:token" element={<SharedSuccessionReadinessPage />} />

        {/* Shared Company Overview - Public Route (VALUTTEN-branded snapshot for parent groups) */}
        <Route path="/companies/shared/:token" element={<SharedCompanyOverviewPage />} />

        {/* Shared Marketing Report - Public Route (no auth required). Counts and
            plain-text commentary only; no commission figures in the payload. */}
        <Route
          path="/reports/marketing-source/shared/:token"
          element={<SharedMarketingSourceReportPage />}
        />

        {/* --- Protected Routes --- */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <MfaEnforcementGuard>
                <AppLayout />
              </MfaEnforcementGuard>
            </ProtectedRoute>
          }
        >
          {/* Default and Core Routes — blocked for hideCommissions brokers */}
          <Route element={<RequireCommissionAccess />}>
            <Route index element={<DashboardPage />} />
          </Route>
          {/* Commission Breakdown: not available to brokers */}
          <Route element={<RequireRole allowedRoles={['super_admin', 'administrator', 'office_admin']} />}>
            <Route path="commission-breakdown" element={<MonthlyTableDashboardPage />} />
          </Route>
          {/* Upload: admin roles only (broker cannot upload) */}
          <Route element={<RequireRole allowedRoles={['super_admin', 'administrator', 'office_admin']} />}>
            <Route path="upload" element={<UploadPage />} />
          </Route>

          {/* --- SFG / Salestrekker Pipeline Tracker ---
              DELIBERATELY UNLISTED. No nav entry on either surface: the founder
              validates by direct URL first. The page itself gates on the server
              entitlement (checkFeatureEntitlement reads the addOnFeatures
              contract grant, which featureFlags does not carry) plus the
              tri-state rollout, where `observe` means reachable but unlisted
              and `enabled` means listed.

              office_admin and the broker family are excluded here as well as in
              the callables: a Salestrekker snapshot is company-wide and carries
              no office dimension to scope by, so an office-scoped role would get
              a full-company view rather than a narrower one. */}
          <Route element={<RequireRole allowedRoles={['super_admin', 'administrator']} />}>
            <Route path="pipeline-tracker" element={<PipelineTrackerPage />} />
          </Route>
          {/* Team Management: admin roles only (broker cannot manage team) */}
          <Route element={<RequireRole allowedRoles={['super_admin', 'administrator', 'office_admin']} />}>
            <Route path="team" element={<TeamManagementSwitch />} />
          </Route>
          <Route path="enroll-2fa" element={<NativeEnroll2FAPage />} />
          {/* Office/Broker management: admin roles only (broker cannot manage) */}
          <Route element={<RequireRole allowedRoles={['super_admin', 'administrator', 'office_admin']} />}>
            <Route path="offices" element={<OfficeManagementPage />} />
            <Route path="offices/compare" element={<OfficeComparisonPage />} />
            <Route path="brokers" element={<BrokerManagementPage />} />
          </Route>
          {/* Office dashboard and broker dashboard — blocked for hideCommissions brokers */}
          <Route element={<RequireCommissionAccess />}>
            <Route
              path="offices/:officeId"
              element={
                <FeatureGate feature="office_dashboard">
                  <OfficeDashboardPage />
                </FeatureGate>
              }
            />
            <Route path="brokers/:brokerId" element={<DashboardPage />} />
          </Route>
          {/* Loan Books: super_admin + administrator */}
          <Route element={<RequireRole allowedRoles={['super_admin', 'administrator']} />}>
            <Route path="loan-books" element={<LoanBooksPage />} />
          </Route>

          {/* AI Assistant — Enterprise: super_admin + administrator, Pro: super_admin only.
              Migrated 2026-05-19 from RequireRole to RequireCapability; legacyAllow
              preserves byte-identical behaviour when the per-tenant kill-switch is
              flipped (companies/{id}.featureFlags.capabilityRbacEnabled === false). */}
          <Route element={<RequireCapability capability="use_ai_assistant" legacyAllow={['super_admin', 'administrator']} />}>
            <Route
              path="ai-assistant"
              element={
                <FeatureGate feature="ai_assistant">
                  <AIAssistantPage />
                </FeatureGate>
              }
            />
          </Route>

          {/* AI First-Login Briefing — super_admin + administrator */}
          <Route element={<RequireRole allowedRoles={['super_admin', 'administrator']} />}>
            <Route path="briefing" element={<FirstLoginBriefingPage />} />
          </Route>

          {/* --- Loyalty Program Routes (super_admin + administrator, company-gated trial) --- */}
          <Route element={<RequireRole allowedRoles={['super_admin', 'administrator']} />}>
            <Route path="loyalty" element={<FeatureGate feature="loyalty_program"><LoyaltyDashboardPage /></FeatureGate>} />
            <Route path="loyalty/clients" element={<FeatureGate feature="loyalty_program"><LoyaltyClientsPage /></FeatureGate>} />
            <Route path="loyalty/redemptions" element={<FeatureGate feature="loyalty_program"><LoyaltyRedemptionsPage /></FeatureGate>} />
            <Route path="loyalty/bulk-activate" element={<FeatureGate feature="loyalty_program"><BulkActivationPage /></FeatureGate>} />
          </Route>
          {/* Loyalty Settings — super_admin only, company-gated trial */}
          <Route element={<RequireRole allowedRoles={['super_admin']} />}>
            <Route path="loyalty/settings" element={<FeatureGate feature="loyalty_program"><LoyaltySettingsPage /></FeatureGate>} />
          </Route>

          {/* --- Founder Portal Route --- */}
          <Route element={<RequireRole allowedRoles={['founder']} />}>
          </Route>

          {/* --- Contact Activity Route (Admin only) --- */}
          <Route element={<RequireRole allowedRoles={['super_admin', 'administrator']} />}>
            <Route path="contact-activity" element={<ContactActivityPage />} />
          </Route>

          {/* --- Client Care — Enterprise-standard / Plus+Pro via featureFlags.client_care; listClients callable enforces RBAC scope.
                 RequireClientCareAccess hides it from broker-family not yet attached to a broker (derived, not a manual toggle). --- */}
          <Route element={<RequireRole allowedRoles={['super_admin', 'administrator', 'office_admin', 'broker', 'broker_assistant', 'credit_analyst']} />}>
            <Route element={<RequireClientCareAccess />}>
              <Route path="clients" element={<FeatureGate feature="client_care"><ClientDatabasePage /></FeatureGate>} />
            </Route>
          </Route>

          {/* --- Client Care Settings (cadences + notifications): admin only + feature-gated; callables enforce role.
                 office_admin is admitted READ-ONLY and only while the tenant carries
                 `featureFlags.office_admin_access_controls`; the page renders every control
                 disabled for that role and the update callable still refuses her, so the
                 route gate is never the only control. --- */}
          <Route element={<RequireRole allowedRoles={['super_admin', 'administrator', 'office_admin']} />}>
            <Route path="client-care-settings" element={<FeatureGate feature="client_care"><ClientCareSettingsPage /></FeatureGate>} />
          </Route>

          {/* --- Partner Referrals (Partner Network). Referrer side: per-tenant
                 `featureFlags.partner_network === true` (strict, default off) AND a role on
                 PARTNER_NETWORK_ROLES. "My referral program" desk (program/*): super_admin,
                 administrator and marketing, admitted only when their company operates an
                 active program (partnerDeskGetContext); marketing gets no other way in.
                 service_staff/loyalty_client never. Same predicate as the sidebar entry
                 (canSeePartnerReferrals); the loyalty callables re-check everything. --- */}
          <Route element={<RequirePartnerReferralsArea />}>
            <Route path="partner-referrals" element={<PartnerReferralsPage />}>
              <Route index element={<PartnerOverviewTab />} />
              <Route path="refer" element={<PartnerReferTab />} />
              <Route path="clients" element={<PartnerClientsTab />} />
              <Route path="clients/:referralId" element={<PartnerReferralDetailPage />} />
              <Route path="rewards" element={<PartnerRewardsTab />} />
              <Route path="radar" element={<PartnerBackBookRadarTab />} />
              <Route path="compliance" element={<PartnerComplianceTab />} />
              <Route path="program/*" element={<MyReferralProgramPage />} />
              <Route path="*" element={<Navigate to="/partner-referrals" replace />} />
            </Route>
          </Route>

          {/* --- Discharged Re-Engagement Settings — admin only; callables enforce role. NOT
              feature-gated here so admins can reach it to enable the feature (default OFF). --- */}
          <Route element={<RequireRole allowedRoles={['super_admin', 'administrator']} />}>
            <Route path="discharged-reengagement-settings" element={<ReengagementSettingsPage />} />
          </Route>

          {/* --- Broker Commission Settings (Admin only) --- */}
          <Route element={<RequireRole allowedRoles={['super_admin', 'administrator']} />}>
            <Route path="broker-commission-settings" element={<BrokerCommissionSettingsPage />} />
          </Route>

          {/* --- My Commission Settings (Broker, gated by allowedReports) --- */}
          <Route element={<RequireReportAccess reportSlug="commission-settings" />}>
            <Route path="my-commission-settings" element={<BrokerCommissionViewPage />} />
          </Route>

          {/* --- Client Sets Route (Admin only) --- */}
          <Route element={<RequireRole allowedRoles={['super_admin', 'administrator']} />}>
            <Route path="client-groups" element={<ClientSetsPage />} />
          </Route>

          {/* --- Grouped Reports Routes --- */}
          <Route path="reports">
            {/* Available to all roles including broker — with granular access control */}
            <Route element={<RequireReportAccess reportSlug="settlement-age" />}>
              <Route path="settlement-age" element={<SettlementAgeReportPage />} />
            </Route>
            <Route element={<RequireReportAccess reportSlug="clawbacks" />}>
              <Route path="clawbacks" element={<ClawbackPage />} />
            </Route>
            {/* Arrears: hideable per tenant (fail-open) for aggregators that
                supply no arrears feed at all, e.g. Finsure. Only an explicit
                `featureFlags.arrears = false` blocks it. */}
            <Route element={<RequireTenantReport flag="arrears" />}>
              <Route element={<RequireReportAccess reportSlug="arrears" />}>
                <Route path="arrears" element={<ArrearsPage />} />
              </Route>
            </Route>
            <Route element={<RequireReportAccess reportSlug="discharged-loans" />}>
              <Route path="discharged-loans" element={<DischargedLoansRouter />} />
            </Route>
            <Route element={<RequireReportAccess reportSlug="loans" />}>
              <Route path="loans" element={<LoanListPage />} />
            </Route>
            <Route element={<RequireReportAccess reportSlug="trail-drop-off" />}>
              <Route
                path="trail-drop-off"
                element={
                  <FeatureGate feature="trail_drop_off">
                    <TrailDropOffPage />
                  </FeatureGate>
                }
              />
            </Route>
            {/* Approved vs Settled: hideable per tenant (fail-open) for
                aggregators with no lender-approved loan amount, so the variance
                can never be computed. Only an explicit
                `featureFlags.approved_vs_settled = false` blocks it. The
                router picks today's page or the v2 page (tenant flag
                `approved_vs_settled_v2 = 'enabled'`) behind the same guards. */}
            <Route element={<RequireTenantReport flag="approved_vs_settled" />}>
              <Route element={<RequireReportAccess reportSlug="approved-vs-settled" />}>
                <Route
                  path="approved-vs-settled"
                  element={<ApprovedVsSettledRouter />}
                />
              </Route>
            </Route>
            {/* Linked deals: aggregator feature (LMG profile + featureFlags.linked_deals);
                the page and linkedDealsAction both enforce it, plus company-wide admin. */}
            <Route path="linked-deals" element={<LinkedDealsPage />} />
            <Route element={<RequireReportAccess reportSlug="book-health" />}>
              <Route
                path="book-health"
                element={
                  <FeatureGate feature="book_health">
                    <BookHealthReportPage />
                  </FeatureGate>
                }
              />
            </Route>
            <Route element={<RequireReportAccess reportSlug="customer-portfolio" />}>
              <Route
                path="customer-portfolio"
                element={
                  <FeatureGate feature="customer_portfolio">
                    <CustomerPortfolioPage />
                  </FeatureGate>
                }
              />
            </Route>

            {/* Refinance Intelligence & Acquired Books: available to all roles */}
            <Route element={<RequireReportAccess reportSlug="refinance-intelligence" />}>
              <Route
                path="refinance-intelligence"
                element={
                  <FeatureGate feature="refinance_intelligence">
                    <RefinanceIntelligenceRouter />
                  </FeatureGate>
                }
              />
            </Route>
            <Route element={<RequireReportAccess reportSlug="acquired-books" />}>
              <Route
                path="acquired-books"
                element={
                  <FeatureGate feature="refinance_intelligence">
                    <AcquiredBooksRouter />
                  </FeatureGate>
                }
              />
            </Route>

            {/* Succession Readiness & Referral Partners (Admin only + Enterprise + Beta flag required) */}
            <Route element={<RequireRole allowedRoles={['super_admin', 'administrator', 'office_admin']} />}>
              <Route
                path="succession-readiness"
                element={
                  <FeatureGate feature="succession_readiness">
                    <SuccessionReadinessReportPage />
                  </FeatureGate>
                }
              />
              <Route
                path="referral-partners"
                element={
                  <FeatureGate feature="referral_partners">
                    <ReferralPartnerPage />
                  </FeatureGate>
                }
              />
            </Route>

            {/* Group Snapshot - super_admin only. Mints a PUBLIC unauthenticated
                link over group-wide commission figures, so it sits in its own
                tighter RequireRole rather than the admin/office_admin block
                below. Tenant gate (featureFlags.company_overview_share) is
                enforced in the page and on both callables. */}
            <Route element={<RequireRole allowedRoles={['super_admin']} />}>
              <Route path="group-snapshot" element={<GroupSnapshotPage />} />
            </Route>

            {/* Reports NOT available to broker role */}
            <Route element={<RequireRole allowedRoles={['super_admin', 'administrator', 'office_admin']} />}>
              <Route path="trail-run-off" element={<TrailRunOffReportPage />} />
              <Route
                path="trail-book-valuation"
                element={
                  <FeatureGate feature="trail_book_valuation">
                    <TrailBookValuationPage />
                  </FeatureGate>
                }
              />
              <Route
                path="trail-forecast"
                element={
                  <FeatureGate feature="trail_forecast">
                    <TrailForecastPage />
                  </FeatureGate>
                }
              />
              <Route
                path="rcti-cashflow"
                element={
                  <FeatureGate feature="rcti_cashflow">
                    <RctiCashflowPage />
                  </FeatureGate>
                }
              />
            </Route>

            {/* Broker Loan Books (Admin only + Enterprise Feature) */}
            <Route element={<RequireRole allowedRoles={['super_admin', 'administrator']} />}>
              <Route
                path="broker-loan-books"
                element={
                  <FeatureGate feature="broker_loan_books">
                    <BrokerLoanBooksPage />
                  </FeatureGate>
                }
              />
              <Route
                path="broker-report/:brokerDisplay"
                element={
                  <FeatureGate feature="broker_loan_books">
                    <BrokerReportPage />
                  </FeatureGate>
                }
              />
            </Route>

            {/* Recludo Fund-Flow Report (Admin only) — gated by per-tenant feature flag */}
            <Route element={<RequireRole allowedRoles={['super_admin', 'administrator']} />}>
              <Route path="recludo" element={<RecludoReportsAdminPage />} />
            </Route>

            {/* Book Value Report (Admin only) — gated by per-tenant
                featureFlags.book_value_report ('off' | 'observe' | 'enabled').
                Sidebar entry surfaces only when 'enabled'. */}
            <Route element={<RequireRole allowedRoles={['super_admin', 'administrator']} />}>
              <Route path="book-value" element={<BookValueReportPage />} />
            </Route>

            {/* Broker Book Trend Report — per-tenant gated by
                featureFlags.broker_book_trend_report ('off' | 'observe' |
                'enabled'). Sidebar surfaces only on 'enabled'. Excludes
                credit_analyst, marketing, founder per plan §4b. Broker-family
                additionally gated by allowedReports.includes('book-trend')
                via the sidebar; the route itself is role-only because the
                callable performs the final allowedReports / RBAC enforcement
                server-side. */}
            <Route element={<RequireRole allowedRoles={['super_admin', 'administrator', 'office_admin', 'broker', 'broker_assistant']} />}>
              <Route path="book-trend" element={<BrokerBookTrendReportPage />} />
            </Route>

            {/* Trail Leakage — the canonical surface is here under Reports.
                Gated by view_trail_leakage (legacyAllow mirrors the prior
                broker-activity gate). The page renders its own G2 definition
                banner. Entitlement is a UNION (trail_leakage OR broker_activity)
                so pro+ tenants get it by tier while existing broker_activity
                holders keep access (decoupled 2026-07-20). Enforced server-side
                by requireTrailLeakageAccess + by this FeatureGate. */}
            {/* A tenant that explicitly suppressed Trail Leakage gets NOTHING, not
                the upgrade card: that card's copy is MyCRM-branded, which is the
                one thing a non-MyCRM tenant must never be shown. Fail-open, so
                only an explicit `featureFlags.trail_leakage = false` bounces. */}
            <Route element={<RequireTenantReport flag="trail_leakage" />}>
              <Route element={<RequireCapability capability="view_trail_leakage" legacyAllow={['super_admin', 'administrator', 'office_admin', 'broker', 'broker_assistant', 'credit_analyst']} />}>
                <Route
                  path="trail-leakage"
                  element={
                    <FeatureGate feature="trail_leakage" anyOf="broker_activity">
                      <TrailLeakagePage />
                    </FeatureGate>
                  }
                />
              </Route>
            </Route>

          </Route>

          {/* Activity Log — lightweight top-level route used by brokers to
              log touchpoints. Stays outside the Lead Capture subapp because
              it's a cross-cutting broker utility. Gated on broker_activity
              add-on subscription. */}
          <Route element={<RequireRole allowedRoles={['super_admin', 'administrator', 'office_admin', 'broker', 'broker_assistant', 'credit_analyst']} />}>
            <Route
              path="activities"
              element={
                <FeatureGate feature="broker_activity">
                  <ActivitiesPage />
                </FeatureGate>
              }
            />
          </Route>

          {/* ---------- Lead Capture add-on subapp (/leads/*) ----------
              Contextual top nav with 9 pages. Entire subtree is FeatureGated
              on the `lead_capture` add-on (paid via Stripe or granted via
              addOnFeatures/promotionalFeatures). Non-subscribers see the
              UpgradePromptCard paywall regardless of which URL they hit. */}
          <Route element={<RequireRole allowedRoles={['super_admin', 'administrator', 'office_admin', 'broker', 'broker_assistant', 'credit_analyst', 'marketing', 'service_staff']} />}>
            <Route path="leads" element={<AddOnSubappLayout addOn="lead_capture" />}>
              {/* Every tab below carries its OWN guard, reading the same
                  config/leadTabAccess map the top nav uses. The parent
                  RequireRole admits the whole broker family + service_staff
                  (they all need /leads/inbox), so without a per-route guard
                  the admin-only tabs were hidden in the nav but reachable by
                  typing the URL. Each map entry mirrors what that page's
                  callables actually enforce, so this can only narrow, never
                  widen. */}
              <Route index element={<Navigate to="/leads/inbox" replace />} />
              <Route element={<RequireLeadTab path="/leads/how-it-works" />}>
                <Route path="how-it-works" element={<LeadCaptureHowItWorksPage />} />
              </Route>
              <Route element={<RequireLeadTab path="/leads/sources" />}>
                <Route path="sources" element={<LeadCaptureSourcesPage />} />
              </Route>
              <Route element={<RequireLeadTab path="/leads/inbox" />}>
                <Route path="inbox" element={<LeadsPage />} />
              </Route>
              <Route element={<RequireLeadTab path="/leads/staff" />}>
                <Route path="staff" element={<LeadStaffPage />} />
              </Route>
              <Route element={<RequireLeadTab path="/leads/attribution" />}>
                <Route path="attribution" element={<LeadAttributionPage />} />
              </Route>
              {/* Marketing monthly report. Lives in /leads (marketing's home
                  subapp), NOT /reports, which UnifiedTopBar denies to the
                  marketing role outright. */}
              <Route element={<RequireLeadTab path="/leads/monthly-report" />}>
                <Route path="monthly-report" element={<MarketingSourceReportPage />} />
              </Route>
              <Route element={<RequireLeadTab path="/leads/email" />}>
                <Route path="email" element={<LeadCaptureEmailPage />} />
              </Route>
              <Route element={<RequireLeadTab path="/leads/templates" />}>
                <Route path="templates" element={<LeadCaptureTemplatesPage />} />
              </Route>
              {/* eDM Campaigns. Hidden and unroutable unless the company's
                  featureFlags.edm_broadcast === true (RequireEdmEnabled, the
                  same predicate as the top nav). Import, contacts and the
                  sending domain (and topics) are company-wide sub-routes, not tabs. Static
                  segments (new, import, contacts, domain, topics) rank above
                  :campaignId, so they are never captured as ids. */}
              <Route element={<RequireEdmEnabled />}>
                <Route element={<RequireLeadTab path="/leads/edm" />}>
                  <Route path="edm" element={<EdmCampaignsPage />} />
                  <Route path="edm/new" element={<EdmCampaignPage />} />
                  <Route element={<RequireEdmImportAccess />}>
                    <Route path="edm/import" element={<EdmImportPage />} />
                    <Route path="edm/contacts" element={<EdmContactsPage />} />
                    <Route path="edm/domain" element={<EdmDomainPage />} />
                    {/* Topics also need edm_preferences; the page redirects
                        to /leads/edm without it (plan-v3 core E2). */}
                    <Route path="edm/topics" element={<EdmTopicsPage />} />
                    {/* Journeys (plan JOURNEYS J8) also need edm_journeys; both
                        pages redirect to /leads/edm without it. */}
                    <Route path="edm/journeys" element={<EdmJourneysPage />} />
                    <Route path="edm/journeys/:journeyId" element={<EdmJourneyPage />} />
                  </Route>
                  <Route path="edm/:campaignId" element={<EdmCampaignPage />} />
                </Route>
              </Route>
              <Route element={<RequireLeadTab path="/leads/setup" />}>
                <Route path="setup" element={<LeadCaptureSetupWizardPage />} />
              </Route>
              <Route element={<RequireLeadTab path="/leads/integrations" />}>
                <Route path="integrations" element={<LeadCaptureIntegrationsPage />} />
              </Route>
              <Route element={<RequireLeadTab path="/leads/cost-savings" />}>
                <Route path="cost-savings" element={<LeadCaptureCostSavingsPage />} />
              </Route>
              <Route element={<RequireLeadTab path="/leads/availability" />}>
                <Route path="availability" element={<MyAvailabilityPage />} />
              </Route>
              <Route element={<RequireLeadTab path="/leads/routing" />}>
                <Route path="routing" element={<LeadRoutingRulesPage />} />
              </Route>
              <Route element={<RequireLeadTab path="/leads/crm-mappings" />}>
                <Route path="crm-mappings" element={<CrmStageMappingsPage />} />
              </Route>
              <Route element={<RequireLeadTab path="/leads/website" />}>
                <Route path="website" element={<WebsiteConnectionsPage />} />
              </Route>
              <Route element={<RequireLeadTab path="/leads/consent" />}>
                <Route path="consent" element={<ConsentTextsPage />} />
              </Route>
              <Route element={<RequireLeadTab path="/leads/unsubscribes" />}>
                <Route path="unsubscribes" element={<UnsubscribesPage />} />
              </Route>
              <Route element={<RequireLeadTab path="/leads/taxonomy" />}>
                <Route path="taxonomy" element={<LeadTaxonomyPage />} />
              </Route>
              <Route element={<RequireLeadTab path="/leads/pipeline" />}>
                <Route path="pipeline" element={<LeadPipelinePage />} />
              </Route>
              {/* VALUTTEN Growth inbox. Hidden and unroutable unless the
                  company's featureFlags.growth === 'propose' AND the
                  lead_capture add-on is granted (RequireGrowthPropose, the
                  same flag predicate as the top nav). The role guard runs
                  first, so a denied role triggers no entitlement call. */}
              <Route element={<RequireLeadTab path="/leads/growth" />}>
                <Route element={<RequireGrowthPropose />}>
                  <Route path="growth" element={<GrowthInboxPage />} />
                </Route>
              </Route>
              {/* Phase E observability: lead SLA dashboard. Read-only.
                  getLeadSlaSummary admits office_admin and is office-scoped,
                  so the map entry includes it. Mounts under
                  AddOnSubappLayout addOn="lead_capture" so non-subscribers
                  see the upgrade paywall. */}
              <Route element={<RequireLeadTab path="/leads/sla" />}>
                <Route path="sla" element={<LeadSlaDashboardPage />} />
              </Route>

              {/* DM Inbox (Meta Messenger Page DMs) — moved here from the
                  standalone /conversations tree (2026-05-25) so it lives
                  inside the Lead Capture CRM add-on and inherits the
                  lead_capture FeatureGate paywall from AddOnSubappLayout.
                  RequireLeadTab keeps the original
                  access_conversations_inbox contract (excludes credit_analyst,
                  who passes the parent /leads RequireRole); the legacy role
                  list now lives in the shared map. Per-tenant behaviour is
                  still governed by featureFlags.meta_messenger_dm.mode at the
                  webhook. */}
              <Route element={<RequireLeadTab path="/leads/dm-inbox" />}>
                <Route path="dm-inbox" element={<ConversationsInboxPage />} />
                <Route path="dm-inbox/:conversationId" element={<ConversationThreadPage />} />
              </Route>
            </Route>
          </Route>

          {/* ---------- Team Activity (/broker-activity/* routes kept) ----------
              Pipeline / Scorecard / Weekly Funnel render under the MAIN app
              shell (AppLayout + UnifiedTopBar), as a section like Reports /
              Offices / Settings — NOT under AddOnSubappLayout (which would
              swap the main top menu for a sub-only nav). UnifiedTopBar
              renders the Team Activity tab strip via getCurrentContext().

              Each page is wrapped in the broker_activity FeatureGate so
              non-subscribers see the UpgradePromptCard paywall, preserving the
              entitlement gate that AddOnSubappLayout used to provide.

              Trail Leakage moved out to /reports/trail-leakage (Reports). The
              /broker-activity index lands on Pipeline (no capability divert). */}
          <Route element={<RequireRole allowedRoles={['super_admin', 'administrator', 'office_admin', 'broker', 'broker_assistant', 'credit_analyst', 'marketing']} />}>
            <Route path="broker-activity">
              <Route index element={<Navigate to="/broker-activity/pipeline" replace />} />
              <Route
                path="overview"
                element={
                  <FeatureGate feature="broker_activity">
                    <BrokerActivityOverviewPage />
                  </FeatureGate>
                }
              />
              <Route
                path="pipeline"
                element={
                  <FeatureGate feature="broker_activity">
                    <PipelinePage />
                  </FeatureGate>
                }
              />
              <Route
                path="scorecard"
                element={
                  <FeatureGate feature="broker_activity">
                    <ScorecardPage />
                  </FeatureGate>
                }
              />
              <Route
                path="weekly-funnel"
                element={
                  <FeatureGate feature="broker_activity">
                    <WeeklyFunnelPage />
                  </FeatureGate>
                }
              />
              <Route
                path="lodgements"
                element={
                  <FeatureGate feature="broker_activity">
                    <LodgementsPage />
                  </FeatureGate>
                }
              />
            </Route>
          </Route>

          {/* --- Acquisition Analysis (Enterprise Feature - Admin only) --- */}
          <Route element={<RequireRole allowedRoles={['super_admin', 'administrator']} />}>
            <Route
              path="acquisition-analysis"
              element={
                <FeatureGate feature="acquisition_analysis">
                  <AcquisitionAnalysisPage />
                </FeatureGate>
              }
            />
          </Route>

          {/* --- Help & Support (all roles incl. marketing — help docs are safe) --- */}
          <Route path="help" element={<HelpPage />} />

          {/* --- Legacy DM Inbox redirects ---
              The DM Inbox now lives inside the Lead Capture CRM subapp at
              /leads/dm-inbox (gated by the lead_capture add-on + the
              access_conversations_inbox capability). These redirects keep
              old /conversations links working; the entitlement/capability
              gates are enforced at the new destination. */}
          <Route path="/conversations" element={<Navigate to="/leads/dm-inbox" replace />} />
          <Route path="/conversations/:conversationId" element={<ConversationDeepLinkRedirect />} />

          {/* --- Grouped Settings & Admin Routes ---
              The /settings index (SettingsPage) is open to ALL roles incl.
              marketing — it tab-gates to Profile / Security (2FA) / Privacy for
              everyone and renders admin tabs only for admins (so marketing can
              change their name, password, 2FA, and consent, like a broker). The
              admin SUB-routes below keep their own RequireRole / RequireCapability
              guards, which exclude marketing. */}
          <Route path="settings">
            <Route index element={<SettingsPage />} />
            {/* Connected Accounts — commission email-ingestion OAuth (Gmail/Outlook).
                Excluded for marketing (CRM email connector lives under /leads).
                Migrated 2026-05-19 from universal to capability-gated. */}
            <Route element={<RequireCapability capability="manage_connected_accounts" legacyAllow={['super_admin', 'administrator', 'office_admin', 'broker', 'broker_assistant', 'credit_analyst']} />}>
              <Route path="connected-accounts" element={<ConnectedAccountsPage />} />
            </Route>
            {/* These routes are only accessible to the roles specified */}
            <Route element={<RequireRole allowedRoles={['super_admin', 'administrator', 'office_admin']} />}>
              <Route path="audit-log" element={<AuditLogPage />} />
            </Route>
            {/* Billing route — super_admin only */}
            <Route element={<RequireRole allowedRoles={['super_admin']} />}>
              <Route path="billing" element={<BillingPage />} />
            </Route>
            {/* Integration status and revocation remain available even if an
                entitlement lapses. Connect controls enforce their own explicit
                server entitlement inside IntegrationsPage. */}
            <Route element={<RequireRole allowedRoles={['super_admin']} />}>
              <Route path="integrations" element={<IntegrationsPage />} />
              <Route path="integrations/zoho/callback" element={<ZohoOAuthCallbackPage />} />
            </Route>
            {/* CRM Mappings (super_admin + administrator; lender/adviser identity seeding).
                Entitlement is the SAME union as the Trail Leakage report/page
                (trail_leakage OR broker_activity): mappings are a required
                companion surface for trail leakage (adviser-to-broker + lender
                name mapping seed the detector), reached via "Open CRM Mappings"
                on the Trail Leakage page. Previously broker_activity-only, which
                paywalled pro-tier tenants (e.g. Decru) that hold trail_leakage by
                tier. Matched to the report so the pair can never diverge again.
                Enforced server-side by requireTrailLeakageAccess on
                crmMappingAction + getCrmMappingCoverage. */}
            {/* Matched to the Trail Leakage report above, including the tenant
                suppression: this page is MyCRM-branded end to end and is inert
                without a CRM connection, so a suppressed tenant must not reach
                it OR its upgrade card. */}
            <Route element={<RequireTenantReport flag="trail_leakage" />}>
              <Route element={<RequireRole allowedRoles={['super_admin', 'administrator']} />}>
                <Route
                  path="crm-mappings"
                  element={
                    <FeatureGate feature="trail_leakage" anyOf="broker_activity">
                      <CrmMappingsPage />
                    </FeatureGate>
                  }
                />
              </Route>
            </Route>
          </Route>

        </Route>

        {/* --- Catch-all Not Found Route --- */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      <CookieConsentBanner />
    </BrowserRouter>
  );
};

export default AppRoutes;
